import React from 'react'
import { DateTime } from 'luxon'

import { Breadcrumbs } from '@amzn/stencil-react-components/breadcrumbs'
import { Col, Row, View } from '@amzn/stencil-react-components/layout'
import {
    ProgressTracker,
    ProgressTrackerStep,
    ProgressTrackerStepStatus,
} from '@amzn/stencil-react-components/progress-tracker'
import { H1, Text } from '@amzn/stencil-react-components/text'

import { IMPORT_STEP_LABELS, ImportStep } from 'src/pages/import-content/ImportContentModel'

const VERSION_ID_DISPLAY_CHARS = 8

const STEPS: ImportStep[] = [ImportStep.Upload, ImportStep.Review, ImportStep.Create]

const stepStatus = (step: ImportStep, currentStep: ImportStep): ProgressTrackerStepStatus => {
    if (step < currentStep) {
        return ProgressTrackerStepStatus.Complete
    }
    return step === currentStep
        ? ProgressTrackerStepStatus.Available
        : ProgressTrackerStepStatus.Disabled
}

export interface ImportContentHeaderProps {
    moduleName?: string
    moduleVersionId?: string
    savedAt?: Date
    currentStep: ImportStep
    onStepSelected: (step: ImportStep) => void
}

export const ImportContentHeader = ({
    moduleName,
    moduleVersionId,
    savedAt,
    currentStep,
    onStepSelected,
}: ImportContentHeaderProps) => {
    const crumbs = [
        ...(moduleVersionId
            ? [
                  {
                      title: 'Module builder',
                      url: `/module-builder?moduleVersionId=${moduleVersionId}`,
                  },
              ]
            : []),
        ...(moduleName ? [{ title: moduleName }] : []),
    ]

    const summary = [
        moduleName && `From ${moduleName}`,
        moduleVersionId && `Version ID ${moduleVersionId.slice(0, VERSION_ID_DISPLAY_CHARS)}`,
        savedAt && `Saved ${DateTime.fromJSDate(savedAt).toFormat("M/d 'at' h:mm a")}`,
    ].filter(Boolean)

    return (
        <Col gridGap='S300' width='100%'>
            {crumbs.length > 0 && <Breadcrumbs crumbs={crumbs} />}
            <Col gridGap='S100'>
                <H1 fontSize='T500'>Import content</H1>
                {summary.length > 0 && (
                    <Text fontSize='T100' color='neutral70' dataTestId='import-content-summary'>
                        {summary.join(' · ')}
                    </Text>
                )}
            </Col>
            <Row width='100%'>
                <View flex={1}>
                    <ProgressTracker
                        aria-label='Import content'
                        dataTestId='import-content-progress'
                        selectedStep={currentStep}
                        onStepSelected={(step) => onStepSelected(step as ImportStep)}
                    >
                        {STEPS.map((step) => (
                            <ProgressTrackerStep
                                key={step}
                                dataTestId={`import-step-${step}`}
                                labelText={IMPORT_STEP_LABELS[step]}
                                status={stepStatus(step, currentStep)}
                            />
                        ))}
                    </ProgressTracker>
                </View>
            </Row>
        </Col>
    )
}
